"use client";

import { useLingui } from "@lingui/react/macro";
import { Alert } from "@offstreetparking/design-system/feedback";
import { Button, FormInput } from "@offstreetparking/design-system/forms";
import { Link } from "@offstreetparking/design-system/typography";
import { Form } from "@offstreetparking/portal-flow/components/forms/Form";
import { useMutation } from "@tanstack/react-query";
import { useId } from "react";
import * as z from "zod/v4";
import { useCompany } from "~/components/providers/CompanyProvider";
import { routes } from "~/routes";
const forgotPasswordSchema = z.object({
	email: z.string().email(),
	companyId: z.number().optional(),
});
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

type ForgotPasswordFormContentProps = {
	onRequestReset: (data: ForgotPasswordFormData) => Promise<void>;
	redirectTo?: string;
	email?: string;
	onSwitchTo?: (target: "signIn") => void;
	onSuccessChange?: (isSuccess: boolean) => void;
};

export const ForgotPasswordFormContent = ({
	onRequestReset,
	redirectTo,
	email,
	onSwitchTo,
	onSuccessChange,
}: ForgotPasswordFormContentProps) => {
	const { t } = useLingui();
	const formId = useId();
	const { company } = useCompany();
	const {
		mutate: requestReset,
		isPending,
		isSuccess,
		error,
	} = useMutation({
		mutationFn: onRequestReset,
		onSuccess: () => onSuccessChange?.(true),
	});

	if (isSuccess) {
		return (
			<div className="space-y-6">
				<Alert
					size="sm"
					type="success"
					title={t`If an account exists for this email, we'll send you a password reset link.`}
					message={
						onSwitchTo ? t`After you reset your password, return to this page and sign in.` : undefined
					}
				/>

				<div className="text-center">
					<BackToSignIn onSwitchTo={onSwitchTo} redirectTo={redirectTo} />
				</div>
			</div>
		);
	}

	return (
		<>
			{error && (
				<Alert
					size="sm"
					type="error"
					title={error.message || t`Failed to send reset link. Please try again.`}
				/>
			)}

			<Form
				id={formId}
				className="space-y-6"
				schema={forgotPasswordSchema}
				initialValues={{
					email: email ?? "",
					companyId: company?.id,
				}}
				onSubmit={(values: ForgotPasswordFormData) => requestReset(values)}
			>
				<FormInput
					name="email"
					type="email"
					label={t`Email Address`}
					placeholder={t`you@example.com`}
					autoComplete="email"
					isRequired
				/>

				<Button isLoading={isPending} type="submit" form={formId} className="w-full">
					{t`Send Reset Link`}
				</Button>

				<div className="text-center">
					<BackToSignIn onSwitchTo={onSwitchTo} redirectTo={redirectTo} />
				</div>
			</Form>
		</>
	);
};

const BackToSignIn = ({
	onSwitchTo,
	redirectTo,
}: {
	onSwitchTo?: (target: "signIn") => void;
	redirectTo?: string;
}) => {
	const { t } = useLingui();

	if (onSwitchTo) {
		return (
			<Button intent="action" type="button" onClick={() => onSwitchTo("signIn")}>
				{t`Back to Sign In`}
			</Button>
		);
	}

	return (
		<Link
			href={routes.login({
				redirectTo,
			})}
		>
			{t`Back to Sign In`}
		</Link>
	);
};
